import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UserPlus, Copy, KeyRound, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Member {
  userId: string;
  email: string;
  name: string;
  role: 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'MANAGER' | 'EMPLOYEE' | 'DEMO';
  canSwitchTenants: boolean;
  disabledAt: string | null;
}

interface Invite {
  id: string;
  email: string;
  role: Member['role'];
  expiresAt: string;
}

export function UsersPage() {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user)!;
  const canEdit = user.isSuperAdmin || user.role === 'TENANT_ADMIN' || user.role === 'MANAGER';

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<Member[]>('/users') });
  const invites = useQuery({ queryKey: ['invites'], queryFn: () => api.get<Invite[]>('/invites') });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Member['role']>('EMPLOYEE');
  const [inviteUrl, setInviteUrl] = useState('');

  const inviteMut = useMutation({
    mutationFn: () => api.post<{ inviteUrl: string }>('/invites', { email: inviteEmail.trim(), role: inviteRole }),
    onSuccess: (r) => {
      setInviteUrl(r.inviteUrl);
      toast.success('Invite sent');
      qc.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed to invite'),
  });

  const resetMut = useMutation({
    mutationFn: (userId: string) => api.post<{ tempPassword: string }>(`/users/${userId}/reset-password`),
    onSuccess: (r, userId) => {
      toast.success('Password reset — temp password copied below', { duration: 10_000 });
      navigator.clipboard?.writeText(r.tempPassword).catch(() => {});
      alert(`Temporary password for this user:\n\n${r.tempPassword}\n\nCopy this now — it won't be shown again.`);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => api.delete(`/invites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });

  const updateRoleMut = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Member['role'] }) => api.patch(`/users/${id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div className="container py-8 space-y-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground">Members of this dealership + pending invites.</p>
        </div>
        {canEdit && (
          <Button onClick={() => { setInviteUrl(''); setInviteEmail(''); setInviteRole('EMPLOYEE'); setInviteOpen(true); }} className="gap-2">
            <UserPlus className="h-4 w-4" /> Invite
          </Button>
        )}
      </header>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Members</h2>
        {users.isLoading && <Skeleton className="h-32" />}
        {users.data && (
          <Card>
            <div className="divide-y divide-border">
              {users.data.map((m) => (
                <div key={m.userId} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{m.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canEdit ? (
                      <Select value={m.role} onValueChange={(v) => updateRoleMut.mutate({ id: m.userId, role: v as Member['role'] })}>
                        <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EMPLOYEE">Employee</SelectItem>
                          <SelectItem value="MANAGER">Manager</SelectItem>
                          <SelectItem value="TENANT_ADMIN">Tenant admin</SelectItem>
                          {user.isSuperAdmin && <SelectItem value="SUPER_ADMIN">Super admin</SelectItem>}
                        </SelectContent>
                      </Select>
                    ) : <Badge variant="outline">{m.role}</Badge>}
                    {canEdit && m.userId !== user.id && (
                      <Button size="icon" variant="ghost" onClick={() => confirm('Reset this user\'s password?') && resetMut.mutate(m.userId)} title="Reset password">
                        <KeyRound className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Pending invites</h2>
        {invites.data && invites.data.length === 0 ? (
          <Card><CardContent className="py-6 text-sm text-muted-foreground">No pending invites.</CardContent></Card>
        ) : invites.data && (
          <Card>
            <div className="divide-y divide-border">
              {invites.data.map((i) => (
                <div key={i.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div>
                    <div className="font-medium">{i.email}</div>
                    <div className="text-xs text-muted-foreground">{i.role} · expires {new Date(i.expiresAt).toLocaleDateString()}</div>
                  </div>
                  {canEdit && (
                    <Button size="icon" variant="ghost" onClick={() => revokeMut.mutate(i.id)} title="Revoke">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </section>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite teammate</DialogTitle>
            <DialogDescription>They'll get an email with a one-week link.</DialogDescription>
          </DialogHeader>
          {inviteUrl ? (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Email sent. You can also share this link directly:</div>
              <div className="flex gap-2">
                <Input readOnly value={inviteUrl} onFocus={(e) => e.currentTarget.select()} />
                <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success('Copied'); }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as Member['role'])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMPLOYEE">Employee</SelectItem>
                    <SelectItem value="MANAGER">Manager</SelectItem>
                    <SelectItem value="TENANT_ADMIN">Tenant admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost">{inviteUrl ? 'Done' : 'Cancel'}</Button></DialogClose>
            {!inviteUrl && (
              <Button onClick={() => inviteMut.mutate()} disabled={!inviteEmail.trim() || inviteMut.isPending}>
                {inviteMut.isPending ? 'Sending…' : 'Send invite'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
